import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Skeleton,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import EditIcon from "@mui/icons-material/Edit";
import Cookies from "js-cookie";
import axios from "axios";
import { useSelector } from "react-redux";
import DeleteJobDialog from "./DeleteJobDialog";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AddIcon from "@mui/icons-material/Add";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import AddCompany from "./addCompany";
import AddBranch from "../components/addBranch";

// ── Shimmer helper ────────────────────────────────────────────────────────────
const Shimmer = ({
  width = "100%",
  height = "16px",
  borderRadius = "6px",
  sx = {},
}) => (
  <Box
    sx={{
      width,
      height,
      borderRadius,
      background:
        "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.4s infinite",
      flexShrink: 0,
      "@keyframes shimmer": {
        "0%": { backgroundPosition: "200% 0" },
        "100%": { backgroundPosition: "-200% 0" },
      },
      ...sx,
    }}
  />
);

// ── Tab panel ─────────────────────────────────────────────────────────────────
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ height: "100%", overflow: "hidden" }}
    >
      {value === index && <Box sx={{ height: "100%" }}>{children}</Box>}
    </div>
  );
}
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// ── Info row ──────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      gap: 2,
      py: "10px",
    }}
  >
    <Typography
      sx={{ width: "140px", flexShrink: 0, fontSize: "13px", color: "#64748B" }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "13px",
        color: "#151D48",
        fontWeight: 500,
        wordBreak: "break-word",
      }}
    >
      {value || "NA"}
    </Typography>
  </Box>
);

// ── Card header style ─────────────────────────────────────────────────────────
const cardHeaderSx = {
  backgroundColor: "#D9EAFB",
  borderRadius: "8px 8px 0 0",
  px: 2.5,
  py: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

// ── Branch row ────────────────────────────────────────────────────────────────
const BranchRow = ({ branch, index, total, onClick, companyLogo }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      px: 2.5,
      py: 1.5,
      borderBottom: index < total - 1 ? "1px solid #F1F5F9" : "none",
      cursor: "pointer",
      "&:hover": { backgroundColor: "#F8FAFC" },
    }}
  >
    <Box
      sx={{
        width: "40px",
        height: "40px",
        flexShrink: 0,
        border: "1px solid #E2E8F0",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#F8FAFC",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {companyLogo ? (
        <img
          src={companyLogo}
          alt="logo"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : (
        <Avatar
          sx={{
            width: 40,
            height: 40,
            borderRadius: "8px",
            backgroundColor: "#E2E8F0",
            color: "#94a3b8",
            fontSize: "14px",
          }}
        >
          {(branch.branch_name || branch.name)?.[0]?.toUpperCase() || "B"}
        </Avatar>
      )}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: "#151D48",
            textTransform: "capitalize",
          }}
        >
          {branch.branch_name || branch.name}
        </Typography>
        {branch.is_default === 1 && (
          <Box
            sx={{
              px: "8px",
              py: "1px",
              border: "1px solid #3F95EA",
              borderRadius: "4px",
              fontSize: "11px",
              color: "#3F95EA",
              fontWeight: 500,
            }}
          >
            Default
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: "12px", color: "#64748B", mt: "2px" }}>
        {[branch.city, branch.state].filter(Boolean).join(", ") || "NA"}
      </Typography>
    </Box>
  </Box>
);

// ── Header Shimmer — JobDetails jaisi style ───────────────────────────────────
const HeaderShimmer = ({ backbutton, setBackbutton, onBack }) => (
  <Box sx={{ backgroundColor: "#fff", borderRadius: "12px", pb: 0, mb: 0 }}>
    {/* Row 1: back + title */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
      <Box
        onClick={onBack}
        onMouseEnter={() => setBackbutton(true)}
        onMouseLeave={() => setBackbutton(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          pl: "6px",
          pr: backbutton ? "10px" : "6px",
          height: "26px",
          border: `1px solid ${backbutton ? "#3F95EA" : "#E2E8F0"}`,
          borderRadius: "6px",
          cursor: "pointer",
          backgroundColor: backbutton ? "#3F95EA" : "#fff",
          transition: "all 0.2s ease",
          overflow: "hidden",
        }}
      >
        <ChevronLeftIcon
          sx={{
            fontSize: "18px",
            color: backbutton ? "#fff" : "#64748B",
            transition: "color 0.2s",
          }}
        />
        {backbutton && (
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#fff",
              whiteSpace: "nowrap",
            }}
          >
            Back
          </Typography>
        )}
      </Box>
      <Shimmer width="160px" height="22px" borderRadius="6px" />
    </Box>

    <Box sx={{ height: "1px", background: "rgba(21, 29, 72, 0.12)", mb: 2 }} />

    {/* Row 2: logo + info + action buttons */}
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      {/* Left */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Shimmer width="63px" height="63px" borderRadius="10px" />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Shimmer width="200px" height="30px" borderRadius="6px" />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Shimmer width="140px" height="20px" borderRadius="20px" />
            <Shimmer width="80px" height="20px" borderRadius="20px" />
          </Box>
        </Box>
      </Box>
      {/* Right: action buttons shimmer */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
      >
        <Shimmer width="64px" height="34px" borderRadius="8px" />
        <Shimmer width="34px" height="34px" borderRadius="8px" />
      </Box>
    </Box>

    {/* Tabs shimmer */}
    <Box
      sx={{ mt: 2, borderBottom: "1px solid rgba(21, 29, 72, 0.12)", pb: 1 }}
    >
      <Box sx={{ display: "flex", gap: 3 }}>
        <Shimmer width="100px" height="14px" borderRadius="4px" />
      </Box>
    </Box>
  </Box>
);

// ── Basic Details Shimmer ─────────────────────────────────────────────────────
const BasicDetailsShimmer = () => (
  <Box
    sx={{
      flex: 1,
      border: "1px solid #E2E8F0",
      borderRadius: "8px",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box sx={cardHeaderSx}>
      <Shimmer
        width="110px"
        height="18px"
        borderRadius="4px"
        sx={{ bgcolor: "rgba(0,0,0,0.08)" }}
      />
    </Box>
    <Box sx={{ px: 2.5, pb: 1 }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            gap: 2,
            py: "10px",
            borderBottom: "1px solid #F1F5F9",
          }}
        >
          <Shimmer width="120px" height="16px" borderRadius="4px" />
          <Shimmer width="55%" height="16px" borderRadius="4px" />
        </Box>
      ))}
    </Box>
  </Box>
);

// ── Branches Shimmer ──────────────────────────────────────────────────────────
const BranchesShimmer = () => (
  <Box
    sx={{
      flex: 1,
      border: "1px solid #E2E8F0",
      borderRadius: "8px",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box sx={cardHeaderSx}>
      <Shimmer
        width="130px"
        height="18px"
        borderRadius="4px"
        sx={{ bgcolor: "rgba(0,0,0,0.08)" }}
      />
      <Shimmer width="110px" height="30px" borderRadius="8px" />
    </Box>
    <Box>
      {Array.from({ length: 4 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 2.5,
            py: 1.5,
            borderBottom: i < 3 ? "1px solid #F1F5F9" : "none",
          }}
        >
          <Shimmer width="40px" height="40px" borderRadius="8px" />
          <Box sx={{ flex: 1 }}>
            <Shimmer width="55%" height="14px" borderRadius="4px" />
            <Shimmer
              width="40%"
              height="12px"
              borderRadius="4px"
              sx={{ mt: "6px" }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

// ── Main Component ────────────────────────────────────────────────────────────
const CompanyDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [companyInfo, setCompanyInfo] = useState({});
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backbutton, setBackbutton] = useState(false);
  const [searchParams] = useSearchParams({ tab: "companyInfo" });

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [Snackbarserverity, setSnackbarServerity] = useState();
  const [editCompanyOpen, setEditCompanyOpen] = useState(false);
  const [addBranchOpen, setAddBranchOpen] = useState(false);

  const managePermissions = useSelector((state) => state?.user.permissions);
  const EditCompanyPermissions = managePermissions["200"];

  const getCompanyDetails = async () => {
    try {
      const response = await axios.get(`/company-info/${id}`, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      setCompanyInfo(response.data.message[0]);
    } catch {}
  };

  const getBranches = async () => {
    try {
      const response = await axios.post(
        `/list-of-branches/${id}`,
        {},
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      const msg =
        response.data.message?.branchListResponse ?? response.data.message;
      setBranches(Array.isArray(msg) ? msg : []);
    } catch {}
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([getCompanyDetails(), getBranches()]);
      setLoading(false);
    };
    fetchAll();
  }, []);

  const handleBack = () => {
    const previousUrl = sessionStorage.getItem("previousUrl");
    if (previousUrl) {
      const url = new URL(previousUrl, window.location.origin);
      navigate(`${url.pathname}${url.search}`);
      sessionStorage.removeItem("previousUrl");
    } else {
      navigate("/Company?page=1");
    }
  };

  const handleBranchClick = (branch) => {
    sessionStorage.setItem("BranchpreviousUrl", window.location.href);
    navigate(
      `/CompanyDetails/${id}/BranchDetails/${branch.id || branch.branch_id}?tab=BranchInfo`,
    );
  };

  const handleCompanyUpdated = async () => {
    setLoading(true);
    await Promise.all([getCompanyDetails(), getBranches()]);
    setLoading(false);
  };

  const handleBranchCreated = async () => {
    setLoading(true);
    await Promise.all([getCompanyDetails(), getBranches()]);
    setLoading(false);
  };

  const infoBadges = [
    companyInfo?.city && companyInfo?.state
      ? `${companyInfo.city}, ${companyInfo.state}`
      : null,
    companyInfo?.branch_count
      ? `${companyInfo.branch_count} Branch${companyInfo.branch_count > 1 ? "es" : ""}`
      : null,
  ].filter(Boolean);

  const canEdit =
    EditCompanyPermissions?.edit === 1 && companyInfo?.is_editable === 1;
  const canDelete =
    EditCompanyPermissions?.delete === 1 && companyInfo?.is_editable === 1;
  const canAddBranch =
    EditCompanyPermissions?.add === 1 && companyInfo?.is_editable === 1;

  return (
    <Box className="pageWrapper">
      {/* ══ HEADER ══ */}
      {loading ? (
        <HeaderShimmer
          backbutton={backbutton}
          setBackbutton={setBackbutton}
          onBack={handleBack}
        />
      ) : (
        <Box
          sx={{ backgroundColor: "#fff", borderRadius: "12px", pb: 0, mb: 0 }}
        >
          {/* Back + Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
              onClick={handleBack}
              onMouseEnter={() => setBackbutton(true)}
              onMouseLeave={() => setBackbutton(false)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                pl: "6px",
                pr: backbutton ? "10px" : "6px",
                height: "26px",
                border: `1px solid ${backbutton ? "#3F95EA" : "#E2E8F0"}`,
                borderRadius: "6px",
                cursor: "pointer",
                backgroundColor: backbutton ? "#3F95EA" : "#fff",
                transition: "all 0.2s ease",
                overflow: "hidden",
              }}
            >
              <ChevronLeftIcon
                sx={{
                  fontSize: "18px",
                  color: backbutton ? "#fff" : "#64748B",
                  transition: "color 0.2s",
                }}
              />
              {backbutton && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#fff",
                    whiteSpace: "nowrap",
                  }}
                >
                  Back
                </Typography>
              )}
            </Box>
            <Typography
              sx={{ fontSize: "20px", fontWeight: 600, color: "#151D48" }}
            >
              Company Details
            </Typography>
          </Box>

          <Box
            sx={{ height: "1px", background: "rgba(21, 29, 72, 0.12)", mb: 2 }}
          />

          {/* Logo + name + actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {/* Left: logo + info */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box
                sx={{
                  width: "64px",
                  height: "64px",
                  flexShrink: 0,
                  border: "1px solid #E2E8F0",
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: "#F8FAFC",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {companyInfo?.logo ? (
                  <img
                    src={companyInfo.logo}
                    alt="Logo"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "10px",
                      backgroundColor: "#E2E8F0",
                      color: "#94a3b8",
                      fontSize: "22px",
                    }}
                  >
                    {companyInfo?.name?.[0]?.toUpperCase() || "C"}
                  </Avatar>
                )}
              </Box>

              {/* Name + badges */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "rgba(0,0,0,0.87)",
                    textTransform: "capitalize",
                    mb: "4px",
                  }}
                >
                  {companyInfo?.name || "NA"}
                </Typography>
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}
                >
                  {infoBadges.map((badge, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        px: "10px",
                        py: "3px",
                        borderRadius: "20px",
                        backgroundColor: "#F1F5F9",
                        color: "#475569",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      <LocationOnOutlinedIcon sx={{ fontSize: "13px" }} />
                      {badge}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Right: Edit + Delete — JobDetails jaisi style ── */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              {/* Edit button */}
              {canEdit && (
                <Box
                  onClick={() => setEditCompanyOpen(true)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    px: "10px",
                    height: "34px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#F8FAFC" },
                  }}
                >
                  <img src="/assets/edit.svg" alt="" />
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 500, color: "#3F95EA" }}
                  >
                    Edit
                  </Typography>
                </Box>
              )}

              {/* Delete button */}
              {canDelete && (
                <>
                  <Box
                    onClick={() => setOpenDeleteDialog(true)}
                    sx={{
                      width: "34px",
                      height: "34px",
                      border: "1px solid #FECACA",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      backgroundColor: "#fff",
                      "&:hover": { backgroundColor: "#FFF5F5" },
                    }}
                  >
                    <img src="/assets/delete.svg" alt="" />
                  </Box>
                  <DeleteJobDialog
                    Deletename={companyInfo.name}
                    id={id}
                    open={openDeleteDialog}
                    handleClose={() => setOpenDeleteDialog(false)}
                    setOpenSnackbar={setOpenSnackbar}
                    setSnackbarMessage={setSnackbarMessage}
                    setSnackbarServerity={setSnackbarServerity}
                  />
                </>
              )}
            </Box>
          </Box>

          {/* Tab */}
          <Box sx={{ mt: 2, borderBottom: "1px solid rgba(21, 29, 72, 0.12)" }}>
            <Tabs
              value={0}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight: "40px",
                "& .MuiTabs-indicator": { height: "2px", borderRadius: "2px" },
                "& .MuiTab-root": {
                  minHeight: "40px",
                  fontSize: "13px",
                  fontWeight: 500,
                  letterSpacing: "1.25px",
                  padding: "6px 12px",
                },
              }}
            >
              <Tab label="Company Info" {...a11yProps(0)} />
            </Tabs>
          </Box>
        </Box>
      )}

      {/* ══ TAB 0: Company Info ══ */}
      <CustomTabPanel value={0} index={0} sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "stretch",
            height: "100%",
          }}
        >
          {loading ? (
            <>
              <BasicDetailsShimmer />
              <BranchesShimmer />
            </>
          ) : (
            <>
              {/* Basic Details */}
              <Box
                sx={{
                  flex: 1,
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={cardHeaderSx}>
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 700, color: "#151D48" }}
                  >
                    Basic Details
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 2.5,
                    pb: 1,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    // gap: "16px",
                  }}
                >
                  <InfoRow label="Name" value={companyInfo?.name} />
                  <InfoRow label="State" value={companyInfo?.state} />
                  <InfoRow label="City" value={companyInfo?.city} />
                  <InfoRow label="Contact Number" value={companyInfo?.phone} />
                  <InfoRow label="Address" value={companyInfo?.address} />
                  <InfoRow label="Website Link" value={companyInfo?.url} />
                  <InfoRow label="Branches" value={companyInfo?.branch_count} />
                  <InfoRow
                    label="Created On"
                    value={
                      companyInfo?.date_created
                        ? new Date(companyInfo.date_created).toLocaleDateString(
                            "en-GB",
                          )
                        : null
                    }
                  />
                  {companyInfo?.additional_info && (
                    <InfoRow
                      label="Additional Info"
                      value={companyInfo.additional_info}
                    />
                  )}
                </Box>
              </Box>

              {/* Branches — sirf is_branch_managed === 1 par */}
              {companyInfo.is_branch_managed === 1 && (
                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={cardHeaderSx}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "#151D48",
                      }}
                    >
                      Branches Details
                    </Typography>
                    {canAddBranch && (
                      <Box
                        onClick={() => setAddBranchOpen(true)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          px: "10px",
                          height: "30px",
                          border: "1px solid #3F95EA",
                          borderRadius: "8px",
                          cursor: "pointer",
                          backgroundColor: "#3F95EA",
                          "&:hover": { backgroundColor: "#2563EB" },
                        }}
                      >
                        <AddIcon sx={{ fontSize: "15px", color: "#fff" }} />
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#fff",
                            letterSpacing: "0.5px",
                          }}
                        >
                          ADD BRANCH
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ overflowY: "auto", flex: 1 }}>
                    {branches.length === 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "120px",
                        }}
                      >
                        <Typography sx={{ fontSize: "13px", color: "#94a3b8" }}>
                          No branches found
                        </Typography>
                      </Box>
                    ) : (
                      branches.map((branch, i) => (
                        <BranchRow
                          key={branch.id || branch.branch_id || i}
                          branch={branch}
                          index={i}
                          total={branches.length}
                          companyLogo={companyInfo?.logo}
                          onClick={() => handleBranchClick(branch)}
                        />
                      ))
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </CustomTabPanel>

      {/* Snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={(e, reason) => {
          if (reason !== "clickaway") setOpenSnackbar(false);
        }}
      >
        <Alert
          severity={Snackbarserverity}
          sx={{ width: "100%" }}
          onClose={() => setOpenSnackbar(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {editCompanyOpen && (
        <AddCompany
          open={editCompanyOpen}
          onClose={() => setEditCompanyOpen(false)}
          onSuccess={handleCompanyUpdated}
          editCompanyId={id}
          editMode
          disableNavigation
        />
      )}
      {addBranchOpen && (
        <AddBranch
          open={addBranchOpen}
          onClose={() => setAddBranchOpen(false)}
          onSuccess={handleBranchCreated}
          companyId={id}
          companyName={companyInfo?.name}
          disableNavigation
        />
      )}
    </Box>
  );
};

export default CompanyDetails;
